/**
 * DailyTransactionSummaryPage — `/mail/transactions/daily`.
 *
 * Ported from `screens/src/pages/mail/DailyTransactionSummary.jsx`. Shows the
 * per-outlet daily summary (registered / delivered / returned / damaged
 * counts + revenue) for a chosen date.
 *
 * Backend wiring (live):
 *   - GET /v1/mail-items/summary/daily?operatorId=&date=
 *   - GET /v1/mail-items/summary/range?operatorId=&from=&to= (range view)
 */
import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, RefreshCw } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { getDailySummary } from '../../lib/api/transaction-summary';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';
import { KPICard, formatNaira } from './shared/MailUI';

export function DailyTransactionSummaryPage(): JSX.Element {
  const [date, setDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [operatorId, setOperatorId] = useState<string>('1');

  const summary = useQuery({
    queryKey: ['mail', 'summary', 'daily', date, operatorId],
    queryFn: () => getDailySummary(parseInt(operatorId, 10), date),
    enabled: date.length > 0 && operatorId.length > 0 && !Number.isNaN(parseInt(operatorId, 10)),
    retry: false,
  });

  const denied = summary.isError && summary.error instanceof ApiError && (summary.error.status === 401 || summary.error.status === 403);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Daily Transaction Summary"
        description="Per-outlet count + revenue rollup."
        actions={
          <button
            type="button"
            onClick={() => summary.refetch()}
            disabled={summary.isFetching}
            className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${summary.isFetching ? 'animate-spin' : ''}`} /> Refresh
          </button>
        }
      />

      <div className="bg-white border border-border rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="dts-date" className="text-xs font-semibold text-muted uppercase flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Date
          </label>
          <input
            id="dts-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg"
          />
        </div>
        <div>
          <label htmlFor="dts-op" className="text-xs font-semibold text-muted uppercase">Operator id</label>
          <input
            id="dts-op"
            type="number"
            value={operatorId}
            onChange={(e) => setOperatorId(e.target.value)}
            className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg"
          />
        </div>
      </div>

      {summary.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          Token lacks <code className="font-mono">Mail/viewDailySummary</code>.
        </DeferredNotice>
      ) : summary.isError ? (
        <DeferredNotice title="Service unreachable">
          {(summary.error as Error).message}
        </DeferredNotice>
      ) : summary.data ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KPICard title="Registered" value={String(summary.data.registered)} />
          <KPICard title="Delivered"  value={String(summary.data.delivered)} tone="success" />
          <KPICard title="In transit" value={String(summary.data.inTransit)} />
          <KPICard title="Returned"   value={String(summary.data.returned)} tone="warning" />
          <KPICard title="Damaged"    value={String(summary.data.damaged)} tone="danger" />
          <KPICard title="Lost"       value={String(summary.data.lost)} tone="danger" />
          <KPICard
            title="Revenue"
            value={formatNaira(summary.data.revenueMinor)}
            tone="success"
            hint="Money in minor units"
          />
        </div>
      ) : null}
    </div>
  );
}
